import { Routes, Route, Navigate } from 'react-router-dom';
import OutletAuthProvider, { useOutletAuth } from './lib/auth/OutletAuthProvider';
import { BrandingProvider } from './lib/branding';
import { ProtectedRoute } from './components/ProtectedRoute';
import { OfflineIndicator } from './components/OfflineIndicator';
import { SessionOnlyWarning } from './components/SessionOnlyWarning';
import { ErrorBoundary, AuthenticationErrorBoundary } from './components/ErrorBoundary';
import OrderTerminal from './pages/order/OrderTerminal';
import CaptureTerminal from './pages/capture/CaptureTerminal';
import EditTerminal from './pages/edit/EditTerminal';
import TerminalSelector from './pages/TerminalSelector';
import { AuthenticationScreen } from './pages/auth/AuthenticationScreen';
import SettingsScreen from './pages/settings/SettingsScreen';
import CashierDashboard from './pages/cashier/CashierDashboard';
import { DailySummaryPage } from './pages/cashier/DailySummaryPage';

/**
 * Inner app component that has access to outlet auth context
 */
function AppRoutes() {
  const { authState } = useOutletAuth();
  
  return (
    <BrandingProvider outletId={authState?.outletId}>
      <div className="min-h-screen bg-background">
        {/* Global offline indicator */}
        <OfflineIndicator />
        
        {/* Session-only mode warning */}
        <SessionOnlyWarning />
        
        <Routes>
          {/* Authentication - outlet and role selection */}
          <Route 
            path="/auth" 
            element={
              <AuthenticationErrorBoundary>
                <AuthenticationScreen />
              </AuthenticationErrorBoundary>
            } 
          />
          
          {/* Terminal selector - choose which terminal mode (customer role) */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute requiredRole="customer">
                <TerminalSelector />
              </ProtectedRoute>
            } 
          />
          
          {/* Order Terminal - Product selection and payment (customer role) */}
          <Route 
            path="/order/*" 
            element={
              <ProtectedRoute requiredRole="customer">
                <OrderTerminal />
              </ProtectedRoute>
            } 
          />
          
          {/* Capture Terminal - Photo capture with camera (capture role) */}
          <Route 
            path="/capture/*" 
            element={
              <ProtectedRoute requiredRole="capture">
                <CaptureTerminal />
              </ProtectedRoute>
            } 
          />
          
          {/* Edit Terminal - Photo editing and printing (customer role) */}
          <Route 
            path="/edit/*" 
            element={
              <ProtectedRoute requiredRole="customer">
                <EditTerminal />
              </ProtectedRoute>
            } 
          />
          
          {/* Cashier Daily Summary - Password-gated sales summary (cashier role) */}
          <Route
            path="/cashier/daily-summary"
            element={
              <ProtectedRoute requiredRole="cashier">
                <DailySummaryPage />
              </ProtectedRoute>
            }
          />
          {/* Cashier Dashboard - Transaction management (cashier role) */}
          <Route
            path="/cashier/*"
            element={
              <ProtectedRoute requiredRole="cashier">
                <CashierDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Settings - All roles can access */}
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <SettingsScreen />
              </ProtectedRoute>
            } 
          />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrandingProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <OutletAuthProvider>
        <AppRoutes />
      </OutletAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
